import Divider from "components/divider";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/Input";
import Sheet from "components/sheet/Sheet";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Radio from "components/radio";
import { ButtonNew as Button } from "components/buttonNew";
import { TGiftCardParam } from "pages/secure/facility/tableService/newTableService/tableServiceCart/TableServiceCart";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "hooks/redux";
import {
  resolveGiftCardExpiryPreview,
  GiftCardExpirySetting,
  getDefaultGiftCardPosOverrideExpiry,
} from "helpers/giftCardExpiry";
import { formatDate, parseLocalDateString } from "helpers/Helpers";
import "./giftCardSell.scss";

interface IGiftCardProps {
  sellGiftCardsActive: boolean;
  onCancel?: () => void;
  onOk?: () => void;
  gift_cards: TGiftCardParam[];
  setGiftCards: (giftCards: Array<TGiftCardParam>) => void;
}

export interface IGiftCardSellState {
  gift_cards: {
    cart_line_item_id: number;
    code: string;
    pin: number;
    random: boolean;
    balance: number;
    reload: number;
  }[];
}

type GiftCardSellMode = "new" | "reload";

function getCardMode(card: TGiftCardParam): GiftCardSellMode {
  return card.reload === 1 ? "reload" : "new";
}

function GiftCardSell(props: IGiftCardProps) {
  const { t } = useTranslation();
  const { facilityStore } = useAppSelector(store => store);
  const [configureSeparately, setConfigureSeparately] = useState(false);

  const facilityExpiryType = (facilityStore?.teeSheetSettings?.gift_card_expiry ?? "no_expiry") as GiftCardExpirySetting;
  const facilityExpiryYears = facilityStore?.teeSheetSettings?.gift_card_expiry_years ?? 5;

  const hasMultipleGiftCards = (props?.gift_cards?.length ?? 0) > 1;
  const useUnifiedSettings = hasMultipleGiftCards && !configureSeparately;

  useEffect(() => {
    if (props.sellGiftCardsActive) {
      setConfigureSeparately(false);
    }
  }, [props.sellGiftCardsActive]);

  function updateGiftCards(
    index: number,
    updater: (card: TGiftCardParam) => TGiftCardParam,
    applyToAll = useUnifiedSettings,
  ) {
    props?.setGiftCards(
      props?.gift_cards?.map((card, i) => {
        if (applyToAll || i === index) {
          return updater(card);
        }
        return card;
      }),
    );
  }

  function onOk() {
    void props.onOk();
  }

  const handleSellGiftCardInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    let { value } = e.target;
    let valueNum = e.target.valueAsNumber;

    if (value === "") {
      value = null;
      valueNum = null;
    }

    updateGiftCards(index, card => ({
      ...card,
      [e.target.name]: e.target.name === "pin" ? valueNum : value,
    }));
  };

  const handleModeChange = (index: number, mode: GiftCardSellMode) => {
    updateGiftCards(index, card => {
      if (mode === "reload") {
        return {
          ...card,
          random: false,
          reload: 1,
          code: card.code ?? null,
          pin: card.pin ?? null,
        };
      }

      return {
        ...card,
        random: true,
        reload: 0,
        code: null,
        pin: null,
      };
    });
  };

  const handleRandomToggle = (index: number, checked: boolean) => {
    updateGiftCards(index, card => {
      if (checked) {
        return {
          ...card,
          random: true,
          reload: 0,
          code: null,
          pin: null,
        };
      }

      return {
        ...card,
        random: false,
        reload: 0,
        code: card.code ?? null,
        pin: card.pin ?? null,
      };
    });
  };

  const handleGiftCardCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, checked } = e.target;

    updateGiftCards(index, card => {
      if (name === "override_expiry") {
        return {
          ...card,
          override_expiry: checked,
          expires_at: checked ? card.expires_at ?? formatDate(getDefaultGiftCardPosOverrideExpiry()) : undefined,
        };
      }

      return { ...card, [name]: checked };
    });
  };

  const handleExpiryDateChange = (date: Date, index: number) => {
    updateGiftCards(index, card => ({ ...card, expires_at: formatDate(date) }));
  };

  function handleUseSameSettingsForAll() {
    const template = props.gift_cards[0];
    if (!template) {
      setConfigureSeparately(false);
      return;
    }

    props?.setGiftCards(
      props.gift_cards.map(card => ({
        ...card,
        random: template.random,
        reload: template.reload,
        code: template.code,
        pin: template.pin,
        override_expiry: template.override_expiry,
        expires_at: template.expires_at,
      })),
    );
    setConfigureSeparately(false);
  }

  function onCancel() {
    props?.onCancel();
  }

  const sellGiftCardFieldsEmpty = !props?.gift_cards?.every(card => {
    if (card.random) {
      return true;
    }

    return card.code != null && card.code !== "" && card.pin != null;
  });

  function renderGiftCardForm(gift_card: TGiftCardParam, index: number, title: string) {
    const mode = getCardMode(gift_card);
    const isReload = mode === "reload";
    const defaultExpiryPreview = resolveGiftCardExpiryPreview(
      facilityExpiryType,
      facilityExpiryYears,
      gift_card.gift_card_expiry_type,
      gift_card.gift_card_expiry_years,
    );

    return (
      <div key={useUnifiedSettings ? "unified" : gift_card.cart_line_item_id} className="gift-card-sell-item">
        <p className="gift-card-sell-item-title">{title}</p>

        <Radio.Group
          className="gift-card-sell-mode-group"
          value={mode}
          onChange={(value: GiftCardSellMode) => handleModeChange(index, value)}
        >
          <Radio.Button value="new">{t("New Gift Card")}</Radio.Button>
          <Radio.Button value="reload">{t("Reload Existing")}</Radio.Button>
        </Radio.Group>

        {isReload ? (
          <div className="gift-card-sell-fields">
            <Input
              value={gift_card.code || ""}
              placeholder={t("Gift card code")}
              name="code"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSellGiftCardInputChange(e, index)}
            />
            <Input
              type="number"
              value={gift_card.pin ?? ""}
              placeholder={t("Gift card pin")}
              name="pin"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSellGiftCardInputChange(e, index)}
            />
          </div>
        ) : (
          <>
            {!gift_card.random ? (
              <div className="gift-card-sell-fields">
                <Input
                  value={gift_card.code || ""}
                  placeholder={t("Gift card code")}
                  name="code"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSellGiftCardInputChange(e, index)}
                />
                <Input
                  type="number"
                  value={gift_card.pin ?? ""}
                  placeholder={t("Gift card pin")}
                  name="pin"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSellGiftCardInputChange(e, index)}
                />
              </div>
            ) : null}
            <Checkbox
              name="random"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRandomToggle(index, e.target.checked)}
              label={t("Generate Random Code")}
              size="small"
              checked={!!gift_card.random}
            />
            {gift_card.random ? (
              <p className="gift-card-sell-helper">
                {useUnifiedSettings
                  ? t("A random gift card code and PIN will be generated for each card.")
                  : t("A random gift card code and PIN will be generated.")}
              </p>
            ) : null}
          </>
        )}

        <div className="gift-card-sell-expiry">
          {!gift_card.override_expiry && defaultExpiryPreview ? (
            <p className="gift-card-sell-expiry-preview">
              {t("Expires: {{date}}", { date: defaultExpiryPreview })}
            </p>
          ) : null}

          <Checkbox
            name="override_expiry"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleGiftCardCheckboxChange(e, index)}
            label={t("Set expiry date")}
            size="small"
            checked={!!gift_card.override_expiry}
          />

          {gift_card.override_expiry ? (
            <DatePickerInput
              months={1}
              position="center"
              usePortal
              startingDate={
                gift_card.expires_at
                  ? parseLocalDateString(gift_card.expires_at) ?? getDefaultGiftCardPosOverrideExpiry()
                  : getDefaultGiftCardPosOverrideExpiry()
              }
              setStartingDate={(date: Date) => handleExpiryDateChange(date, index)}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <Sheet
      title={t("Enter Gift Card Numbers")}
      open={props.sellGiftCardsActive}
      size="small"
      closable
      onCancel={onCancel}
      onOk={onOk}
      okText={t("Add Gift Cards")}
      okDisabled={sellGiftCardFieldsEmpty}
    >
      <div className="gift-card-sell">
        {useUnifiedSettings ? (
          renderGiftCardForm(
            props.gift_cards[0],
            0,
            t("Gift Cards ({{count}})", { count: props.gift_cards.length }),
          )
        ) : (
          props?.gift_cards?.map((gift_card, index: number) => (
            <React.Fragment key={gift_card.cart_line_item_id}>
              {renderGiftCardForm(
                gift_card,
                index,
                `${t("secure.facility.table_service.table_service.154")} ${index + 1}`,
              )}
              {index !== props.gift_cards.length - 1 && <Divider className="gift-card-sell-divider" />}
            </React.Fragment>
          ))
        )}

        {hasMultipleGiftCards ? (
          <div className="gift-card-sell-toggle-mode">
            {configureSeparately ? (
              <Button type="link" size="small" onClick={handleUseSameSettingsForAll}>
                {t("Use same settings for all gift cards")}
              </Button>
            ) : (
              <Button type="link" size="small" onClick={() => setConfigureSeparately(true)}>
                {t("Configure each gift card separately")}
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

export default GiftCardSell;
